import { Tag } from "@pepperstone/pepper-ui-kit";
import { classNames, type TestDataProps } from "@repo/ui";
import type { CashierTradingAccountMargin } from "@/api/cashier";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { usePlatformIcon } from "../usePlatformIcon";
import { Icon } from "@pepperstone/pepper-ui-kit";
import styles from "./AccountItem.module.css";

type AccountItem = {
  account: CashierTradingAccountMargin;
  bordered?: boolean;
  active?: boolean;
  preview?: boolean;
  amountField?: "margin" | "balance";
  onClick?: () => void;
  isMobile?: boolean;
} & TestDataProps;

export const AccountItem = ({
  account,
  bordered,
  active,
  preview,
  amountField = "margin",
  onClick,
  "data-testid": testId,
  isMobile,
}: AccountItem) => {
  const { formatBankingAmount } = useFormatAmount();
  const icon = usePlatformIcon(account.tradingPlatform);

  if (preview) {
    return (
      <div
        data-testid={testId}
        className={classNames([
          styles["account-item-header"],
          styles.preview,
          isMobile && styles.mobile,
        ])}
      >
        <div
          className={classNames([styles["account-item-icon"], styles.preview])}
          data-testid={`${testId}-icon-container`}
        >
          <img src={icon} alt="icon" />
        </div>

        {!isMobile && (
          <Tag bgColor="foreground-neutral" label={account.accountType} />
        )}
        <span className={styles["account-item-id"]}>
          {account.accountLoginId}
        </span>
        {!isMobile && (
          <span className={styles["account-item-nickname-preview"]}>
            {account.accountNickname || account.accountName}
          </span>
        )}
        {isMobile && (
          <div
            className={classNames([
              styles["account-item-icon"],
              styles.preview,
            ])}
            data-testid={`${testId}-arrow-icon-container`}
          >
            <Icon color="black" icon="arrow-right" size={"tiny"} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={() => onClick?.()}
      className={classNames([
        styles["account-item"],
        bordered && styles.bordered,
        active && styles.active,
      ])}
      data-testid={testId}
    >
      <div className={styles["account-item-content"]}>
        <div className={styles["account-item-header"]}>
          <div
            className={styles["account-item-icon"]}
            data-testid={`${testId}-icon-container`}
          >
            {icon && <img src={icon} alt="icon" />}
          </div>

          <Tag bgColor="foreground-neutral" label={account.accountType} />
          <span className={styles["account-item-id"]}>
            {account.accountLoginId}
          </span>
          <span className={styles["account-item-nickname"]}>
            {account.accountNickname || account.accountName}
          </span>
        </div>

        <div className={styles["account-item-amount"]}>
          {formatBankingAmount(
            amountField === "balance" ? account.balance : account.margin
          )}
        </div>
      </div>
    </div>
  );
};
